import moment from 'moment';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { date } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { getSpeedColor, useFormatSpeed } from './utils';

const StyledCalendarDay = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100px;
    height: 100%;
    ${(props) => (props.$hasRightBorder ? 'border-right: 1px solid #dee2e6;' : '')}
    ${(props) => (props.$hasBottomBorder ? 'border-bottom: 1px solid #dee2e6;' : '')}
    width: calc(100% / 7);
    padding: 8px 12px;
    background-color: ${(props) => (props.$hasDayMax ? 'white' : '#ededed')};
`;

const Calendar = ({ className, endDate, inGameSpeeds, yearMaxes, maxTwoWeekSpeed, maxSpeed, showMph }) => {
    const { t } = useTranslation();
    const { formatSpeed } = useFormatSpeed();

    const endDateMoment = useMemo(() => moment(endDate).endOf('day'), [endDate]);
    const twoWeeksAgoMoment = useMemo(() => endDateMoment.clone().subtract(14, 'days'), [endDateMoment]);

    const { twoWeekDayMaxes, twoWeekMaxEffortCounts } = useMemo(() => inGameSpeeds.reduce((all, x) => {
        if (x.isflagged || !moment(x.gamedate).isBetween(twoWeeksAgoMoment, endDateMoment, null, '[]')) return all;
        const gamedate = x.gamedate.substring(0, 10);
        if ((all.twoWeekDayMaxes[gamedate] && x.maxspeed > all.twoWeekDayMaxes[gamedate].maxspeed) || !all.twoWeekDayMaxes[gamedate]) {
            all.twoWeekDayMaxes[gamedate] = x;
        }
        if (x.maxspeed / maxSpeed >= 0.88) {
            all.twoWeekMaxEffortCounts[gamedate] = (all.twoWeekMaxEffortCounts[gamedate] ?? 0) + 1;
        }
        return all;
    }, { twoWeekDayMaxes: {}, twoWeekMaxEffortCounts: {} }), [inGameSpeeds, twoWeeksAgoMoment, endDateMoment, maxSpeed]);

    const yearMaxGroups = useMemo(() => yearMaxes.reduce((all, max) => {
        if (all[max.year]) {
            all[max.year].push(max);
        } else {
            all[max.year] = [max];
        }
        return all;
    }, {}), [yearMaxes]);

    return (
        <div className={`border rounded bg-white py-3 px-4 ${className}`}>
            <div className="d-flex align-items-center">
                {Object.keys(yearMaxGroups).sort().map((year) => (
                    <div key={year} className="d-flex align-items-center me-4">
                        <div className="fw-bold fs-6 lh-1">{year} {t('sections.post-game.in-game-speed.max')}:&nbsp;</div>
                        <div className="fw-bold fs-5 lh-1">{formatSpeed(yearMaxGroups[year][0].maxspeed, showMph)}</div>
                        <div className="fs-5 lh-1">
                            &nbsp;({yearMaxGroups[year].map((x) => t('format.datetime', {
                                val: new Date(x.gamedate), formatParams: { val: { month: 'short', day: 'numeric' } },
                            })).join(', ')})
                            {yearMaxGroups[year][0].isdenominator ? '*' : ''}
                        </div>
                    </div>
                ))}
                <div className={`d-flex align-items-center ${yearMaxGroups.length ? ' ms-4' : ''}`}>
                    <div className="fw-bold fs-6 lh-1">{t('sections.post-game.in-game-speed.two-week-max')}:&nbsp;</div>
                    <div className="fw-bold fs-5 lh-1">{formatSpeed(maxTwoWeekSpeed, showMph)}</div>
                </div>
            </div>
            {maxSpeed && <div>{t('sections.post-game.in-game-speed.denominator-asterisk')}</div>}
            <div className="mt-2 border rounded w-100 d-flex flex-wrap overflow-hidden">
                {Array.from({ length: 14 }).map((_, i) => {
                    const calendarDate = new Date(endDate);
                    calendarDate.setDate(endDate.getDate() - 13 + i);
                    const dayMax = twoWeekDayMaxes[date(calendarDate, 'YYYY-MM-DD')];
                    const exertionCount = twoWeekMaxEffortCounts[date(calendarDate, 'YYYY-MM-DD')];

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledCalendarDay key={i} $hasRightBorder={i % 7 !== 6} $hasBottomBorder={i < 7} $hasDayMax={!!dayMax}>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                                {t('format.datetime', { val: new Date(calendarDate), formatParams: { val: { month: 'short', day: 'numeric' } } })}
                            </div>
                            {dayMax ? (
                                <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                                    <div
                                        className="fs-6 text-center fw-bold"
                                        style={{ color: getSpeedColor(dayMax.maxspeed / maxSpeed) }}
                                    >
                                        {`${formatSpeed(dayMax.maxspeed, showMph)}${exertionCount ? ` (${exertionCount})` : ''}`}
                                    </div>
                                    {maxSpeed && dayMax.maxspeed && (
                                        <div style={{ fontSize: 13 }}>
                                            {`${(dayMax.maxspeed / maxSpeed * 100).toString().split('.')[0]}% ${t('sections.post-game.in-game-speed.of-max')}`}
                                        </div>
                                    )}
                                    <div>{`${dayMax.playerteamid === dayMax.hometeamid ? 'vs.' : '@'}
                                        ${dayMax.playerteamid === dayMax.hometeamid ? dayMax.awayteamabbr : dayMax.hometeamabbr}`}
                                    </div>
                                </div>
                            ) : <div className="d-flex align-items-center justify-content-center flex-grow-1 fs-6">--</div>}
                        </StyledCalendarDay>
                    );
                })}
            </div>
        </div>
    );
};

export default Calendar;
